import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Bell } from "@phosphor-icons/react";

export function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const ref = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/notifications");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const openItem = async (n) => {
    if (!n.read) {
      try { await api.post(`/admin/notifications/${n.id}/read`); } catch { /* silent */ }
    }
    setOpen(false);
    const dest = n.type === "payout_failed" ? "/admin?tab=payroll"
      : (n.type || "").startsWith("contractor_") ? "/admin?tab=employees"
      : "/admin?tab=schedule";
    navigate(dest);
    load();
  };

  const markAll = async () => {
    try { await api.post("/admin/notifications/read-all"); } catch { /* silent */ }
    load();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 hover:bg-muted transition-colors"
        title="Notifications"
        data-testid="notification-bell"
      >
        <Bell size={20} weight={unread > 0 ? "fill" : "regular"} />
        {unread > 0 && (
          <span
            className="absolute top-0 right-0 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold text-white bg-destructive rounded-full"
            data-testid="notification-unread-count"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-80 bg-card border-2 border-border shadow-xl z-[60]"
          data-testid="notification-panel"
        >
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-border">
            <span className="label-caps">Notifications</span>
            {unread > 0 && (
              <button onClick={markAll} className="text-xs text-primary hover:underline" data-testid="mark-all-read">
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground" data-testid="notification-empty">
                No notifications yet.
              </div>
            )}
            {items.map((n) => (
              <button
                key={n.id}
                onClick={() => openItem(n)}
                className={`w-full text-left px-4 py-3 border-b border-border hover:bg-muted/50 transition-colors ${n.read ? "opacity-60" : "bg-primary/5"}`}
                data-testid={`notification-item-${n.id}`}
              >
                <div className="flex items-start gap-2">
                  {!n.read && <span className="mt-1.5 w-2 h-2 rounded-full bg-destructive shrink-0" />}
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">{n.title}</div>
                    <div className="text-xs text-muted-foreground line-clamp-2 break-words">{n.body}</div>
                    <div className="text-[10px] text-muted-foreground mt-1 font-mono">
                      {new Date(n.created_at).toLocaleString()}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
